// Tiny event bus that fires once per browser the first time a Hub
// generation surface (chat / image / research / analyze) produces a
// successful output. Used by `FirstOutputSignupGate` to prompt anonymous
// visitors to create an account so their work is saved.
//
// We persist the "fired" flag in localStorage so the prompt never
// re-appears on this device — even after page reload, sign-out, or a
// later visit. Listeners can still subscribe; we just never emit again.
const FIRED_KEY = "hl_first_output_signup_fired";

type Listener = (surface: string) => void;
const listeners = new Set<Listener>();

const hasFired = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(FIRED_KEY) === "1";
  } catch {
    return false;
  }
};

const markFired = () => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(FIRED_KEY, "1");
  } catch {
    /* private mode — best effort */
  }
};

/**
 * Notify subscribers that a Hub surface produced its first successful
 * output. No-op if the signup prompt has already been shown on this
 * device (per-browser, by design — once is enough).
 */
export function emitFirstOutput(surface: string) {
  if (hasFired()) return;
  markFired();
  listeners.forEach((fn) => {
    try {
      fn(surface);
    } catch {
      /* never let a bad listener break others */
    }
  });
}

export function subscribeFirstOutput(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}